/**
 * The summary band every console screen wears above its content.
 *
 * One component rather than one per screen, because the question a band
 * answers is the same on all of them - is this area healthy, and is there
 * anything here for me right now - and an operator who has learnt to read it
 * on one screen should not have to learn it again on the next.
 *
 * Three rules it enforces rather than documents:
 *
 * - Two to four figures. `BandFigures` is a tuple union, so a fifth figure is
 *   a type error rather than a review comment. The overview carried eleven
 *   once and said nothing; a band is a band because it is short.
 * - Proportion and trend are drawn, not printed. A figure that is part of a
 *   whole carries its bar, and a band with a time series draws its columns.
 *   Plain SVG with an HTML overlay for the hit areas, the same way
 *   `charts.tsx` does it - nothing new is added to
 *   the dependency list to draw a rectangle.
 * - A figure the screen cannot stand behind is `absent`, which renders an em
 *   dash and the sentence saying why. Never a zero: a confident zero over a
 *   table nothing writes ends an investigation that should have started.
 */

import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { cx } from '../utils/cx';
import { Quantity, formatCount, plural } from './measures';
import { StatTile } from './primitives';
import type { Tone } from './status';

const TONE_VARIABLE: Record<Tone, string> = {
  success: 'var(--color-bg-success-solid)',
  warning: 'var(--color-bg-warning-solid)',
  error: 'var(--color-bg-error-solid)',
  info: 'var(--color-bg-brand-solid)',
  neutral: 'var(--color-fg-quaternary)',
};

export type BandFigure = {
  /** Stable key for the tile. */
  id: string;
  /** The precise term leads; a `Term` inside it carries the explanation. */
  label: ReactNode;
  /** Already formatted, e.g. `1,204`. Ignored when `absent` is set. */
  value?: ReactNode;
  /** The unit as the operator reads it. Every figure in a band carries one. */
  unit?: string;
  /** The exact underlying value, kept one hover away from a formatted display. */
  raw?: string | number;
  hint?: ReactNode;
  tone?: Tone;
  /** What the tone badge says. The bare tone word names a colour, not a fact. */
  statusLabel?: string;
  /** The whole this figure is part of, drawn under it. */
  bar?: { fraction: number; label: string; tone?: Tone };
  /** What the figure is counted over, when the label cannot say it in two words. */
  tooltip?: string;
  /**
   * There is no honest figure here, and this is why - a read that has not
   * arrived, or a table with no live writer. Rendered instead of a number.
   */
  absent?: string;
};

/**
 * Two, three or four. Spelled as a tuple union so the limit is checked by the
 * compiler rather than remembered.
 */
export type BandFigures =
  | readonly [BandFigure, BandFigure]
  | readonly [BandFigure, BandFigure, BandFigure]
  | readonly [BandFigure, BandFigure, BandFigure, BandFigure];

/** One period of a band's trend. An empty period is a zero, never a gap closed up. */
export type TrendPoint = { label: string; value: number };

/**
 * Timestamps sliced into evenly spaced periods across the whole span they
 * cover, so a period in which nothing happened still gets a column of its own.
 *
 * Closing an empty period up would merge two different facts - a quiet hour
 * and a gap in reporting - into one smooth line, which is exactly the shape
 * that hides an outage.
 */
export function trendFrom(times: readonly number[], periods = 16): TrendPoint[] {
  const finite = times.filter((value) => Number.isFinite(value));
  if (!finite.length) return [];
  const since = Math.min(...finite);
  const until = Math.max(...finite);
  const count = Math.max(1, periods);
  // A single instant gives a zero-width span, which would put everything in
  // the last slot; a minute keeps the columns evenly spaced instead.
  const width = Math.max(1, (until - since) / count) || 60_000;
  const points: TrendPoint[] = Array.from({ length: count }, (_, index) => {
    const start = since + index * width;
    const date = new Date(start);
    return {
      label:
        width < 86_400_000
          ? date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
          : date.toLocaleDateString(undefined, { month: 'short', day: '2-digit' }),
      value: 0,
    };
  });
  for (const time of finite) {
    const index = Math.min(count - 1, Math.max(0, Math.floor((time - since) / width)));
    const point = points[index];
    if (point) point.value += 1;
  }
  return points;
}

/**
 * The sentence a figure counted over one loaded page owes the reader.
 *
 * Several of these screens page by cursor and cannot ask the server for a
 * total, so their band figures are counted over the rows in hand. That is a
 * useful figure and a misleading one if its population is invisible - it sits
 * beside figures counted over a whole table and reads as the same kind of
 * number. This goes on the tile's tooltip and says which it is.
 */
export function overLoadedRows(rows: number): string {
  return `Counted over the ${formatCount(rows)} ${plural(rows, 'row')} loaded on this screen, not over the whole table.`;
}

const COLUMNS: Record<number, string> = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 xl:grid-cols-3',
  4: 'sm:grid-cols-2 xl:grid-cols-4',
};

/**
 * A count over time as columns, sized against the tallest period so two
 * periods are comparable by height. Every period keeps its slot whether or not
 * anything happened in it: a quiet hour and a gap in reporting are different
 * facts and must not be smoothed into one another.
 */
export function BandTrend({
  points,
  noun,
  caption,
  tone = 'info',
}: {
  points: readonly TrendPoint[];
  /** What one item is, for the readout: `events`, `messages`. */
  noun: string;
  caption?: ReactNode;
  tone?: Tone;
}) {
  const [readout, setReadout] = useState<number | undefined>(undefined);
  const [active, setActive] = useState(0);
  const hits = useRef(new Map<number, HTMLButtonElement>());
  const peak = Math.max(1, ...points.map((point) => point.value));
  const total = points.reduce((sum, point) => sum + point.value, 0);
  const width = points.length ? 100 / points.length : 100;
  const move = (next: number) => {
    const clamped = Math.min(Math.max(next, 0), points.length - 1);
    setActive(clamped);
    hits.current.get(clamped)?.focus();
  };
  const sentenceFor = (point: TrendPoint) =>
    point.value
      ? `${point.label}: ${formatCount(point.value)} ${point.value === 1 ? noun.replace(/s$/, '') : noun}.`
      : `${point.label}: nothing recorded.`;
  const shown = readout === undefined ? undefined : points[readout];
  if (!points.length) return null;
  return (
    <figure className="m-0 flex flex-col gap-2 rounded-xl border border-secondary bg-primary p-4">
      {caption ? <figcaption className="text-sm text-tertiary">{caption}</figcaption> : null}
      <div className="relative h-20 w-full border-b border-secondary">
        <svg
          className="h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {points.map((point, index) => {
            const height = (point.value / peak) * 100;
            return (
              <g key={`col-${point.label}-${index}`}>
                <rect
                  x={index * width}
                  width={Math.max(width - 0.4, 0.2)}
                  y={0}
                  height={100}
                  fill="var(--color-bg-secondary)"
                />
                {point.value > 0 ? (
                  <rect
                    x={index * width}
                    width={Math.max(width - 0.4, 0.2)}
                    y={100 - height}
                    height={Math.max(height, 0.8)}
                    fill={TONE_VARIABLE[tone]}
                  />
                ) : null}
              </g>
            );
          })}
        </svg>
        <div
          role="group"
          aria-label="Counts over time. Use the arrow keys to read each period."
          className="absolute inset-0"
          onMouseLeave={() => setReadout(undefined)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowRight') {
              event.preventDefault();
              move(active + 1);
            } else if (event.key === 'ArrowLeft') {
              event.preventDefault();
              move(active - 1);
            } else if (event.key === 'Home') {
              event.preventDefault();
              move(0);
            } else if (event.key === 'End') {
              event.preventDefault();
              move(points.length - 1);
            }
          }}
        >
          {points.map((point, index) => (
            <button
              key={`hit-${point.label}-${index}`}
              type="button"
              data-trend-index={index}
              data-trend-value={point.value}
              tabIndex={index === active ? 0 : -1}
              ref={(element) => {
                if (element) hits.current.set(index, element);
                else hits.current.delete(index);
              }}
              aria-label={sentenceFor(point)}
              className="absolute top-0 bottom-0 outline-brand focus-visible:outline-2"
              style={{ left: `${index * width}%`, width: `${Math.max(width, 0.2)}%` }}
              onMouseEnter={() => setReadout(index)}
              onFocus={() => {
                setActive(index);
                setReadout(index);
              }}
              onBlur={() => setReadout(undefined)}
            />
          ))}
        </div>
      </div>
      <div className="flex justify-between text-xs text-quaternary tabular-nums">
        <span>{points[0]?.label ?? '—'}</span>
        <span>{points.at(-1)?.label ?? '—'}</span>
      </div>
      <div className="min-h-9 rounded-lg border border-secondary bg-secondary px-3 py-2">
        <p className="text-sm text-secondary select-text" aria-live="polite">
          {shown
            ? sentenceFor(shown)
            : `Point at a column, or tab in and use the arrow keys, to read one period. ${formatCount(total)} ${noun} across ${formatCount(points.length)} periods.`}
        </p>
      </div>
      <p className="sr-only">
        {points.map((point) => `${point.label}: ${formatCount(point.value)}`).join(', ')}
      </p>
    </figure>
  );
}

/**
 * The band itself: two to four figures, and optionally the trend behind them.
 *
 * `label` names the band for a screen reader and is never a second page title.
 * It says what the figures are about rather than repeating the screen's name.
 */
export function MetricBand({
  label,
  figures,
  trend,
  note,
}: {
  label: string;
  figures: BandFigures;
  trend?: {
    points: readonly TrendPoint[];
    noun: string;
    caption?: ReactNode;
    tone?: Tone;
  };
  /** A standing caveat about the whole band - the population, a stale read. */
  note?: ReactNode;
}) {
  return (
    <section aria-label={label} className="flex flex-col gap-3">
      <div className={cx('grid grid-cols-1 gap-3', COLUMNS[figures.length])}>
        {figures.map((figure) => (
          <StatTile
            key={figure.id}
            label={figure.label}
            value={
              figure.absent ? (
                <span className="text-tertiary">—</span>
              ) : (
                <Quantity
                  value={figure.value ?? '—'}
                  {...(figure.unit ? { unit: figure.unit } : {})}
                  {...(figure.raw === undefined ? {} : { raw: figure.raw })}
                />
              )
            }
            {...(figure.absent
              ? { hint: figure.absent }
              : figure.hint
                ? { hint: figure.hint }
                : {})}
            {...(figure.absent || !figure.tone ? {} : { tone: figure.tone })}
            {...(figure.absent || !figure.statusLabel ? {} : { statusLabel: figure.statusLabel })}
            {...(figure.absent || !figure.bar ? {} : { bar: figure.bar })}
            {...(figure.tooltip ? { tooltip: figure.tooltip } : {})}
          />
        ))}
      </div>
      {trend ? <BandTrend {...trend} /> : null}
      {note ? <p className="text-sm text-tertiary">{note}</p> : null}
    </section>
  );
}
